import type { Catalog } from "./catalog.js";

export const en: Catalog = {
  "app.name": "Beacon",
  // Onboarding — Promise
  "onboarding.promise.tagline": "Neighbors warning neighbors.",
  "onboarding.promise.proof.corroboration": "×14 median corroboration on alerts",
  "onboarding.promise.proof.time": "under 90s to a corroborated alert",
  "onboarding.promise.proof.nosignup": "0 accounts, phone numbers, or sign-ups required",
  "onboarding.promise.continue": "Get started",
  "onboarding.promise.restore": "Restore my identity",
  "onboarding.promise.step": "WELCOME",
  // Onboarding — Permissions (JIT-honest)
  "onboarding.perms.step": "STEP 2 / 3",
  "onboarding.perms.title": "How Beacon asks",
  "onboarding.perms.location": "Location is used only to watch your area. We ask the first time you center the map.",
  "onboarding.perms.notifications": "Notifications carry alerts. We ask the first time you opt in.",
  "onboarding.perms.background": "Background refresh keeps alerts timely. Optional, asked in context.",
  "onboarding.perms.later": "Set up later",
  "onboarding.perms.continue": "Continue",
  // Onboarding — Home
  "onboarding.home.step": "STEP 3 / 3",
  "onboarding.home.title": "Where's home?",
  "onboarding.home.radius": "Watch radius",
  "onboarding.home.km": "{n} km",
  "onboarding.home.pickHint": "Move the map so the pin marks this place.",
  "onboarding.home.confirm": "Watch this area",
  "onboarding.home.browse": "Just browse for now",
  "onboarding.home.locating": "Finding your location…",
  "onboarding.home.located": "Pin set to your current location — adjust if needed.",
  "onboarding.home.unavailable": "Location unavailable — move the map to your home.",
  // Now tab — status
  "now.meta": "{place} · {radius} km",
  "now.meta.ago": "{m} min ago",
  "now.meta.justNow": "just now",
  "now.meta.none": "no events yet",
  "now.meta.net": "net {v}",
  "now.meta.netDown": "net —",
  "now.meta.info.title": "What these numbers mean",
  "now.meta.info.radius": "{radius} km — the watch radius of your home zone.",
  "now.meta.info.area": "{area} — when the last event for your home zone arrived (an incident, spread, weather or help update).",
  "now.meta.info.net": "net {net} — when the last event was ingested network-wide, from any feed or neighbour across Europe. If this moves and yours does not, the network is fine and simply has nothing new for you.",
  "now.meta.info.feeds": "Feeds behind what you see: {feeds}.",
  "now.place.default": "your area",
  "region.unsupported.title": "Outside the mapped region",
  "region.unsupported.body": "Map tiles aren't available at this location yet.",
  "now.status.step": "NOW",
  "now.status.allClear": "All clear.",
  "now.status.stayAware": "Stay aware.",
  "now.status.nearby.fire": "Fire nearby.",
  "now.status.nearby.flood": "Flooding nearby.",
  "now.status.nearby.earthquake": "Earthquake felt.",
  "now.status.nearby.conflict": "Danger nearby.",
  "now.status.nearby.other": "Hazard nearby.",
  "now.status.nearby.traffic": "Traffic disruption nearby.",
  "now.status.nearby.storm": "Storm nearby.",
  "now.status.nearby.snow": "Snow nearby.",
  "now.status.nearby.ice": "Ice on roads nearby.",
  "now.status.nearby.heat": "Extreme heat warning.",
  "now.status.nearby.fog": "Dense fog nearby.",
  "now.status.nearby.hazmat": "Hazardous-materials alert nearby.",
  "now.status.nearby.outage": "Utility outage nearby.",
  "now.status.sub": "{distance} km · {place}",
  "now.status.sub.allClear": "Nothing active within your watch radius.",
  // Now tab — community strip
  "now.community.neighbors": "{n} neighbors",
  "now.community.watching": "WATCHING",
  "now.community.helpers": "{n} helpers",
  "now.community.helpersLabel": "NEARBY",
  "now.community.median": "<90s",
  "now.community.medianLabel": "TO CORROBORATED ALERT",
  "now.community.corroborated": "×{n}",
  "now.community.corroboratedLabel": "CORROBORATED",
  // Now tab — activity + report
  "now.activity.title": "NEARBY",
  "now.activity.empty": "Nothing nearby right now.",
  "now.pulse.live": "Network live — last event {s} s ago",
  "now.pulse.quiet": "Network quiet for {m} min",
  "now.pulse.unreachable": "Can’t reach the relay",
  "now.pulse.area": "Your area: last report {m} min ago",
  "now.pulse.areaNone": "Your area: no reports yet",
  "now.zone.addHere": "You’re outside your watched places — add here as a zone?",
  "now.activity.corroboration": "CORROBORATED ×{n}",
  "now.group.areas": "{n} areas",
  "now.group.expand": "Show areas",
  "now.report.button": "Report",
  "now.recenter": "Recenter on me",
  "now.fitAll": "Fit all places",
  "now.raw.toggle": "Raw reports",
  "now.raw.list.title": "Raw reports ({n})",
  "now.raw.list.more": "+{n} more",
  "now.raw.detail": "{hazard} · via {feed} · {time}",
  "now.report.title": "Report",
  "now.report.unverified": "Unverified report — not yet corroborated",
  "now.report.event": "Event",
  "now.report.response": "Response",
  "now.report.severity": "Severity",
  "now.report.observed": "Observed",
  "now.report.source": "Source",
  "now.warning.viewSource": "View full warning ↗",
  // Report sheet — corroborate mode
  "report.corroborate.title": "Corroborate",
  "report.corroborate.q": "Is this the {hazard} near {place}?",
  "report.corroborate.yes": "Yes, I see it too",
  "report.corroborate.no": "No, this is different",
  // Report sheet — full flow
  "report.title": "Report a hazard",
  "report.step.what": "What's happening?",
  "report.step.where": "Where?",
  "report.step.howbad": "How bad?",
  "report.where.here": "Here (your area)",
  "report.where.honesty": "Shared as your neighborhood, not your address.",
  "report.where.pick": "Pick a spot on the map",
  "report.where.pickHint": "Drag the map so the pin sits on the spot.",
  "report.where.current": "Current location",
  "report.where.home": "Home",
  "report.where.pin": "Pin on map",
  "report.where.selected": "Reporting at",
  "report.where.locating": "Locating…",
  "report.where.locateFailed": "Couldn't get your location — reporting at home instead.",
  "report.note.placeholder": "Add one line (optional)",
  "report.next": "Next",
  "report.back": "Back",
  "report.submit": "Send report",
  "report.cancel": "Cancel",
  "report.severity.label": "Severity",
  // Report sheet — confirmation
  "report.done.title": "Sent",
  "report.done.body": "Visible to neighbors now. Alerts fire when others corroborate.",
  "report.done.close": "Done",
  "report.error": "Couldn't send — you can try again.",
  // Hazards (controlled vocab)
  "hazard.fire": "Fire",
  "hazard.flood": "Flood",
  "hazard.earthquake": "Earthquake",
  "hazard.conflict": "Danger",
  "hazard.other": "Hazard",
  "hazard.traffic": "Traffic", "hazard.storm": "Storm", "hazard.snow": "Snow",
  "hazard.ice": "Ice", "hazard.heat": "Heat", "hazard.fog": "Fog",
  "hazard.hazmat": "Hazardous materials", "hazard.outage": "Outage",
  // Chrome
  "tab.now": "Now",
  "tab.help": "Help",
  "tab.you": "You",
  "tab.talk": "Talk",
  "tab.beacon": "Beacon settings",
  "beacon.tab.step": "SETTINGS",
  "beacon.tab.title": "Beacon",
  // Talk tab
  "talk.scope.area": "My area",
  "talk.scope.region": "Greater region",
  "talk.region.size": "Region size: ~{km} km",
  "talk.band.area": "in your area",
  "talk.band.region": "your region · ~{km} km",
  "talk.band.distant": "distant",
  "talk.you": "You",
  "talk.empty": "No messages yet. Say hello to your area.",
  "zone.checking": "Checking your area…",
  "zone.stalled": "Can’t reach the network yet — still trying…",
  "zone.progress.windows": "backlog {done}/{total}",
  "zone.progress.attempts": "attempt {done}/{total}",
  "zone.retry": "Retry",
  "talk.placeholder.locked": "🔒 message unavailable",
  "talk.composer.placeholder": "Message your area…",
  "talk.composer.send": "Send",
  "talk.new.count": "{n} new ↓",
  "talk.zone.home": "Home",
  "talk.zone.nearby": "Nearby",
  "talk.compose.noCell": "Waiting for this place's location…",
  "banner.simulation": "SIMULATION",
  "locale.switch": "Language",
  // Alert receipt — toast/takeover (C4)
  "alert.toast.new": "{hazard} reported nearby",
  "alert.toast.escalation": "{hazard} nearby just escalated",
  "alert.toast.inPath": "{hazard} now moving toward you",
  "alert.toast.reAlert": "{hazard} nearby — update",
  "alert.toast.allClear": "All clear near {place}",
  "alert.toast.sub": "{distance} km · {place}",
  "alert.toast.tap": "Tap for details and safety steps",
  "alert.toast.more": "+{n} more",
  "alert.severity.info": "ADVISORY",
  "alert.severity.warning": "WARNING",
  "alert.severity.critical": "CRITICAL",
  "alert.takeover.title": "{hazard} — take action now",
  "alert.takeover.sub": "{distance} km from {place}",
  "alert.takeover.steps": "SAFETY STEPS",
  "alert.takeover.details": "See full details",
  "alert.takeover.dismiss": "Dismiss",
  "alert.takeover.dismissNext": "Dismiss & see next · {n} more",
  // IncidentDetail (C4)
  "incident.detail.metrics.corroborated": "×{n}",
  "incident.detail.reports.title": "Reports behind this ({n})",
  "incident.detail.metrics.corroboratedLabel": "CORROBORATED",
  "incident.detail.metrics.first": "{ago}",
  "incident.detail.metrics.firstLabel": "FIRST REPORT",
  "incident.detail.metrics.spread": "{status}",
  "incident.detail.metrics.spreadLabel": "SPREAD",
  "incident.detail.spread.moving": "Moving ~{speed} km/h",
  "incident.detail.spread.stationary": "Stationary",
  "incident.detail.spread.dissipating": "Easing",
  "incident.detail.spread.unknown": "—",
  "incident.detail.steps": "SAFETY STEPS",
  "incident.detail.directions": "Directions",
  "incident.detail.share": "Share",
  "incident.detail.help": "I can help",
  "incident.detail.helpSoon": "Help coordination arrives soon.",
  "incident.detail.flag": "Report a problem with this alert",
  "incident.detail.flagSoon": "Flagging arrives soon.",
  "incident.detail.back": "Back",
  "incident.detail.away": "{hazard} — {n} km away",
  "incident.detail.ago.min": "{n} min ago",
  "incident.detail.ago.hr": "{n} h ago",
  "incident.detail.ago.now": "just now",
  // IncidentDetail — details block (origin / how bad / status) + corroborate
  "incident.detail.meta.title": "DETAILS",
  "incident.detail.meta.origin": "Origin",
  "incident.detail.meta.severity": "How bad",
  "incident.detail.meta.status": "Status",
  "incident.detail.origin.crowd": "Community",
  "incident.detail.origin.authority": "Official",
  "incident.severity.1": "Minor",
  "incident.severity.2": "Serious",
  "incident.severity.3": "Severe",
  "incident.status.open": "Active",
  "incident.status.sustained": "Ongoing",
  "incident.status.closing": "Easing",
  "incident.status.closed": "Closed",
  "incident.detail.seeIt": "I see it too",
  "incident.detail.seeItDone": "Thanks — your report was added.",
  "incident.detail.tab.safety": "Safety",
  "incident.detail.tab.details": "Details",
  "incident.detail.tab.actions": "Actions",
  "incident.detail.helpBrowse": "Browse help requests",
  // Safety steps — tone-invariant, per hazard (GUI §3)
  "safety.fire.1": "Leave early — don't wait to see flames.",
  "safety.fire.2": "Go upwind and uphill of the smoke.",
  "safety.fire.3": "Take water, medication, and your phone.",
  "safety.fire.4": "If trapped, find cleared ground or a building.",
  "safety.flood.1": "Move to higher ground now.",
  "safety.flood.2": "Never walk or drive through moving water.",
  "safety.flood.3": "Turn off power if water is rising indoors.",
  "safety.flood.4": "Keep clear of rivers, drains, and bridges.",
  "safety.earthquake.1": "Drop, cover, and hold on until shaking stops.",
  "safety.earthquake.2": "Stay away from windows and heavy furniture.",
  "safety.earthquake.3": "After shaking, check for gas leaks and damage.",
  "safety.earthquake.4": "Expect aftershocks — be ready to cover again.",
  "safety.conflict.1": "Move away from the danger and find cover.",
  "safety.conflict.2": "Stay low and keep solid walls between you and it.",
  "safety.conflict.3": "Share your status with someone you trust.",
  "safety.conflict.4": "Follow local emergency guidance.",
  "safety.other.1": "Move away from the immediate hazard.",
  "safety.other.2": "Warn neighbors who may not have seen it.",
  "safety.other.3": "Follow local emergency guidance.",
  "safety.traffic.1": "Slow down and keep extra distance.",
  "safety.traffic.2": "Follow signs and merge early.",
  "safety.traffic.3": "Consider an alternative route.",
  "safety.storm.1": "Stay indoors and away from windows.",
  "safety.storm.2": "Avoid travel until it passes.",
  "safety.storm.3": "Secure loose outdoor objects.",
  "safety.storm.4": "Keep clear of trees and power lines.",
  "safety.snow.1": "Avoid non-essential travel.",
  "safety.snow.2": "Dress warm; keep a charged phone.",
  "safety.snow.3": "Clear exits and check on neighbours.",
  "safety.ice.1": "Take care on roads and paths.",
  "safety.ice.2": "Slow down; brake gently.",
  "safety.ice.3": "Wear shoes with grip.",
  "safety.heat.1": "Drink water often.",
  "safety.heat.2": "Stay in shade or cool rooms.",
  "safety.heat.3": "Avoid exertion at midday.",
  "safety.heat.4": "Check on the elderly and vulnerable.",
  "safety.fog.1": "Slow down and use low-beam lights.",
  "safety.fog.2": "Keep extra distance.",
  "safety.fog.3": "Avoid overtaking.",
  "safety.hazmat.1": "Go indoors and close windows and vents.",
  "safety.hazmat.2": "Avoid the affected area.",
  "safety.hazmat.3": "Follow official instructions.",
  "safety.outage.1": "Conserve phone battery.",
  "safety.outage.2": "Use torches, not candles.",
  "safety.outage.3": "Check on neighbours who may need help.",
  "offline.strip": "OFFLINE · LAST SYNC {time}",
  // Help tab — need types (controlled vocab)
  "need.medical": "Medical",
  "need.rescue": "Rescue",
  "need.transport": "Transport",
  "need.shelter": "Shelter",
  "need.supplies": "Supplies",
  "need.hands": "Hands",
  "need.other": "Other",
  // Help tab — status block (GUI §4)
  "help.status.step": "HELP",
  "help.status.some": "{n} people need help nearby.",
  "help.status.one": "1 person needs help nearby.",
  "help.status.none": "No open requests nearby.",
  "help.status.sub": "Nearest: {need} · {distance} km",
  "help.status.subNone": "You'll see open requests in your area here.",
  // Helper mode card
  "help.helper.toggle": "I can help",
  "help.helper.intro": "Offer to help neighbors near you.",
  "help.helper.tagsLabel": "What can you help with?",
  "help.helper.ttlLabel": "For how long?",
  "help.helper.ttl.today": "Today",
  "help.helper.ttl.week": "This week",
  "help.helper.ttl.incident": "During this incident",
  "help.helper.area": "Your area only — coarse location, never your address.",
  "help.helper.register": "Register availability",
  "help.helper.registered": "You're available to help. We'll surface nearby requests.",
  "help.helper.tierUnknown": "TRUST · grows as you help and get vouched",
  // Requests near you (matcher A7 push, private SSE)
  "help.nearby.title": "Requests near you",
  "help.nearby.toast": "Someone nearby needs {need}",
  "help.nearby.empty": "No matches yet — you'll be notified.",
  "help.offer.toast": "Someone offered to help",
  "help.accept.error": "Couldn't accept yet — still syncing that helper. Try again in a moment.",
  // Open requests list
  "help.list.title": "OPEN REQUESTS",
  "help.list.empty": "No open requests in your area.",
  "help.list.verified": "◆",
  // Request detail + offer/accept (GUI §4)
  "help.detail.back": "Back",
  "help.detail.needLabel": "Needs {need}",
  "help.detail.urgencyLabel": "URGENCY",
  "help.detail.location": "Coarse area only",
  "help.detail.honesty": "Exact address is shared only if they accept your offer.",
  "help.detail.offer": "Offer to help",
  "help.detail.submitOffer": "Send offer",
  "help.detail.notePlaceholder": "Add a note (optional)",
  "help.detail.etaPlaceholder": "ETA in minutes (optional)",
  "help.detail.offersTitle": "OFFERS",
  "help.detail.offersEmpty": "No offers yet. Neighbors will see this request.",
  "help.detail.accept": "Accept",
  "help.detail.accepted": "Accepted",
  "help.detail.acceptCaveat": "Accepting shares your exact location with that helper — they're not identity-checked, and it can't be undone.",
  "help.detail.offerSent": "Offer sent. You'll be notified if they accept.",
  // Create request sheet
  "help.create.button": "Ask for help",
  "help.create.title": "Ask for help",
  "help.create.step.need": "What do you need?",
  "help.create.step.what": "What's needed?",
  "help.create.step.urgency": "How urgent?",
  "help.create.headlinePlaceholder": "One line — what do you need?",
  "help.create.peoplePlaceholder": "How many people?",
  "help.create.urgencyLabel": "URGENCY",
  "help.create.next": "Next",
  "help.create.back": "Back",
  "help.create.submit": "Post request",
  "help.create.error": "Couldn't post that. Try again.",
  "help.create.done.title": "Request posted.",
  "help.create.done.body": "Neighbors nearby can see it now and offer to help.",
  "help.create.done.close": "Done",
  // My activity
  "help.activity.title": "YOUR ACTIVITY",
  "help.activity.mine": "Your request",
  "help.activity.helping": "You're helping",
  "help.activity.offered": "You offered",
  "help.activity.pending": "Not yet on the network",
  "help.screen.resolvedDone": "Resolved — marked done.",
  "help.screen.resolvedCancelled": "This request was cancelled.",
  "help.screen.loading": "Loading request…",
  "help.activity.activeTitle": "ACTIVE",
  "help.activity.resolvedTitle": "RESOLVED",
  "help.activity.badge.done": "Done",
  "help.activity.badge.cancelled": "Cancelled",
  "help.activity.badge.active": "Active",
  // Coordination thread (GUI §4, §6)
  "help.thread.back": "Back",
  "help.thread.title": "Coordination",
  "help.thread.precise": "Shared location",
  "help.thread.preciseSelf": "Your shared location",
  "help.thread.heartbeat": "{name} · {state}",
  "help.thread.composePlaceholder": "Message…",
  "help.thread.send": "Send",
  "help.thread.done": "Done",
  "help.thread.cantMake": "Can't make it",
  "help.thread.you": "You",
  "help.thread.requester": "Requester",
  "help.thread.helper": "Helper · {id}",
  "help.thread.resend": "Resend",
  "help.thread.empty": "Coordinate here. Messages are encrypted to everyone on this request.",
  "help.thread.openMap": "Open map ↗",
  "help.confirm.cancel": "Cancel",
  "help.confirm.done.title": "Mark this request done?",
  "help.confirm.done.body": "The helper is notified and it moves to Resolved.",
  "help.confirm.done.confirm": "Mark done",
  "help.confirm.cant.title": "Withdraw your help?",
  "help.confirm.cant.body": "The request reopens for other helpers nearby.",
  "help.confirm.cant.confirm": "Withdraw",
  "help.status.chip.open": "Open",
  "help.status.chip.accepted": "Accepted",
  "help.status.chip.enroute": "En route",
  "help.status.chip.arrived": "Arrived",
  "help.status.chip.partial": "Partial",
  "help.status.chip.done": "Done",
  "help.status.chip.cancelled": "Cancelled",
  // Lifecycle step labels (numbered-steps)
  "help.lifecycle.open": "Open",
  "help.lifecycle.offered": "Offered",
  "help.lifecycle.accepted": "Accepted",
  "help.lifecycle.enroute": "En route",
  "help.lifecycle.arrived": "Arrived",
  "help.lifecycle.done": "Done",
  // You tab (GUI §5)
  "you.step": "YOU",
  "you.title": "You & your settings",
  // Location & alerts
  "you.loc.title": "LOCATION & ALERTS",
  "you.loc.home": "Home area",
  "you.loc.setHome": "Set home location",
  "you.loc.coords": "Coordinates",
  "you.loc.change": "Change home area",
  "you.loc.setHomeHint": "Pick a home area to see the map and get alerts for what's near you.",
  "you.loc.radius": "Watch radius",
  "you.loc.km": "{n} km",
  "you.loc.quiet": "Quiet hours",
  "you.loc.quietValue": "{start}:00 – {end}:00",
  "you.loc.quietOff": "Off",
  "you.loc.quietToggle": "Mute non-critical alerts overnight",
  "you.loc.autoDismiss": "Dismiss alerts after",
  "you.loc.autoDismiss.seconds": "{n}s",
  "you.loc.autoDismiss.never": "Stay until dismissed",
  "you.loc.minsev": "Minimum severity to alert",
  "you.loc.addPlace": "Add another place",
  "you.loc.addCurrent": "Use current location",
  "you.loc.liveFollow": "Follow my location",
  "you.loc.liveFollowHint": "Alerts you about hazards where you are while Beacon is open. Uses more battery; off by default.",
  "you.loc.liveFollowUnavailable": "Location access is off. Enable it in your browser to follow your location.",
  "you.loc.keepAwake": "Keep screen on while following",
  "you.loc.keepAwakeHint": "Keeps the display awake during live-follow so an alert is never missed. Turn it off and a sleeping device can't warn you — a web-app limitation.",
  "you.sound.title": "Alert sound",
  "you.sound.hint": "Plays a short tone in your chosen voice when an alert appears. Off until you turn it on; always silent during quiet hours.",
  "you.sound.preview": "Play sample",
  "you.loc.travelRadius": "Travel radius",
  "you.loc.places": "MONITORED PLACES",
  "you.loc.placeUnnamed": "Saved place",
  "you.loc.removePlace": "Remove",
  "now.watched.title": "Other watched places",
  "now.watched.alert": "Fire near {place}",
  "you.loc.editPlace": "Edit",
  "you.loc.placeName": "Name",
  "you.loc.homeName": "Home name",
  "you.loc.moveUp": "Move up",
  "you.loc.moveDown": "Move down",
  // Voice (supporter-locked)
  "you.voice.title": "VOICE",
  "you.voice.intro": "Choose how Beacon talks to you.",
  "you.voice.official": "Official",
  "you.voice.human": "Human",
  "you.voice.dark": "Dark humor",
  "you.voice.deadpan": "Deadpan",
  "you.voice.nonna": "Nonna",
  "you.voice.preview.official": "Wildfire reported 3 km north. Corroborated by 14 neighbors.",
  "you.voice.preview.human": "There's a fire just north of you — your neighbors are seeing it too.",
  "you.voice.preview.dark": "Fire to the north. Good a time as any to test those running shoes.",
  "you.voice.preview.deadpan": "Fire to the north. Noted.",
  "you.voice.preview.nonna": "There's a fire just north, cuore — go now, I mean it.",
  // Trust
  "you.trust.title": "TRUST",
  "you.trust.tier": "Your tier",
  "you.trust.tierUnknown": "— projector-fed (D2)",
  "you.trust.how": "How it grows: account age, confirmed reports, and vouches.",
  "you.trust.factor.age": "Account age",
  "you.trust.factor.confirmed": "Confirmed reports",
  "you.trust.factor.vouches": "Vouches",
  "you.trust.factorValue": "— projector-fed (D2)",
  "you.trust.vouch": "Vouch for a neighbor",
  "you.trust.vouchHint": "Share this link in person with someone you trust.",
  "you.trust.vouchers": "Your vouchers",
  "you.trust.vouchersEmpty": "— projector-fed (D2)",
  "you.trust.backup": "Key backup & rotation",
  "you.trust.export": "Download my recovery file",
  "you.trust.exportHint": "This file is your account. Keep it somewhere safe and private.",
  "you.trust.import": "Restore from a recovery file",
  "you.trust.rotate": "Rotate my keys",
  "you.trust.rotateHint": "Replaces your signing keys. Download a fresh recovery file afterward.",
  "you.trust.rotated": "Keys rotated. Download your new recovery file now.",
  "you.trust.importBad": "That file isn't a valid Beacon recovery file.",
  "you.trust.importOk": "Recovery file accepted. It will be used next launch.",
  "you.trust.capability.direct": "Your reports alert your area directly.",
  "you.trust.capability.needsConfirm": "Right now a neighbour needs to confirm your report before it alerts the area.",
  "you.trust.capability.full": "Your reports already carry full weight. Larger alerts still take a few neighbours confirming — that's how Beacon stays trustworthy.",
  "you.trust.progress.confirmed": "After {confirmed} more confirmed report(s) your reports will carry more weight.",
  "you.trust.progress.days": "Keep using Beacon for about {days} more day(s) to build standing.",
  // Privacy
  "you.privacy.title": "PRIVACY",
  "you.privacy.consentTitle": "What you've agreed to",
  "you.consent.alerts": "Receive alerts for my area",
  "you.consent.helperAvailability": "Share my helper availability",
  "you.consent.preciseLocationRelease": "Release precise location when I accept a request",
  "you.consent.publicMetrics": "Contribute to anonymous public metrics",
  "you.privacy.export": "Export my data",
  "you.privacy.exportHint": "A portable copy of everything on this device.",
  "you.privacy.erase": "Erase me",
  "you.privacy.eraseHint": "Crypto-shred: your keys are destroyed and your data goes dark everywhere.",
  // Safety & privacy disclosure — what the help chat protects + honest limits.
  "you.safety.title": "SAFETY & PRIVACY",
  "you.safety.intro": "What's protected when you ask for or offer help — and where the limits are.",
  "you.safety.toggle": "How your safety is protected",
  "you.safety.e2ee.h": "Messages are end-to-end encrypted",
  "you.safety.e2ee.d": "Only you and the person you coordinate with can read them — not Beacon, not the relay.",
  "you.safety.location.h": "Your exact location stays private until you accept",
  "you.safety.location.d": "Helpers see only your rough area. The moment you accept someone, only they receive your precise spot.",
  "you.safety.metadata.h": "The network can see that you're coordinating",
  "you.safety.metadata.d": "Your words and exact spot are hidden, but the fact you're getting help, your approximate area, and the timing are visible. Treat your neighbourhood as public.",
  "you.safety.identity.h": "Helpers aren't identity-checked",
  "you.safety.identity.d": "Anyone can offer help under any name. Share sensitive details only once you're confident who you're dealing with.",
  "you.safety.irrevocable.h": "Sharing your precise location can't be undone",
  "you.safety.irrevocable.d": "Once you accept, that person keeps the location you shared. There's no take-back.",
  "you.safety.forwardSecrecy.h": "Old messages rely on your device staying secure",
  "you.safety.forwardSecrecy.d": "Beacon doesn't yet use a fresh key for every message, so a stolen key could expose past chats. Keep your phone locked.",
  "you.safety.retention.h": "Messages age out, but you can't delete them",
  "you.safety.retention.d": "Coordination messages disappear on their own after a while. There's no manual per-message delete yet.",
  // Erase confirmation (grave)
  "you.erase.title": "Erase yourself from Beacon?",
  "you.erase.body": "This destroys the keys that unlock everything you've shared. Reports and messages already on neighbors' phones can't be recalled, but everything else goes permanently dark. This cannot be undone.",
  "you.erase.confirmLabel": "Type ERASE to confirm",
  "you.erase.confirmWord": "ERASE",
  "you.erase.cancel": "Keep my account",
  "you.erase.confirm": "Erase me",
  "you.erase.done": "Your erasure request is signed and on its way. Goodbye.",
  // Support Beacon
  // App
  "you.app.title": "APP",
  "you.app.language": "Language",
  "you.app.notifications": "Notifications",
  "you.app.notificationsOn": "On",
  "you.app.notificationsOff": "Off",
  "you.notif.enabled": "Notifications on. You'll be alerted even when Beacon is closed.",
  "you.notif.denied": "Notifications are blocked. Allow them in your browser settings to be alerted when Beacon is closed.",
  "you.notif.unsupported": "This browser can't show notifications when Beacon is closed. In-app alerts still work.",
  "you.notif.nopush": "Notifications allowed, but push isn't set up on this server yet. In-app alerts still work.",
  "you.notif.subfail": "Notifications allowed, but this device couldn't enable push. In-app alerts still work.",
  "you.notif.subfail.noservice": "Notifications allowed, but this device has no push service (e.g. no Google Play). In-app alerts work; background alerts need Chrome, or Beacon's upcoming app.",
  "you.notif.dismissed": "No alerts when Beacon is closed until you allow notifications. Toggle it on any time.",
  "you.app.about": "About Beacon",
  "you.app.version": "Pilot build",
  // Flags & appeals (only if non-empty)
  "you.flags.title": "YOUR FLAGS & APPEALS",
  // E2 moderation — flag sheet
  "flag.title": "Report a problem",
  "flag.intro": "Flags are private. The author is never told who flagged.",
  "flag.reason.falseReport": "False or misleading",
  "flag.reason.spam": "Spam",
  "flag.reason.harassment": "Harassment",
  "flag.reason.dangerousAdvice": "Dangerous advice",
  "flag.reason.harvestAttempt": "Trying to harvest locations",
  "flag.reason.doxxing": "Doxxing / personal info",
  "flag.reason.illegal": "Illegal content",
  "flag.reason.other": "Something else",
  "flag.submit": "Send flag",
  "flag.done": "Thanks — moderators will take a look.",
  "flag.error": "Could not send. Try again.",
  // E2 moderation — You transparency surface
  "you.flags.empty": "Nothing here. No actions on your reports.",
  "you.flags.actionLabel": "Action on your activity",
  "you.flags.weight": "Shown lower in ranking",
  "you.flags.reversible": "This is reversible and time-boxed. You can appeal.",
  "you.flags.appeal": "Appeal this",
  "you.flags.expires": "Expires {date}",
  // E2 moderation — appeal sheet
  "appeal.title": "Appeal",
  "appeal.intro": "Explain why this should be reversed. A moderator reviews it.",
  "appeal.statement.placeholder": "Why is this wrong?",
  "appeal.submit": "Send appeal",
  "appeal.done": "Appeal sent. You'll see the outcome here.",
  "appeal.error": "Could not send the appeal. Try again.",
  // E9 incident detail — trend / freshness / source / advanced (Task 3)
  "incident.detail.meta.lastConfirmed": "Last confirmed",
  "incident.detail.meta.wind": "Wind",
  "incident.detail.meta.trend": "Trend",
  "incident.detail.meta.sources": "Sources",
  "incident.detail.trend.rising": "Intensifying",
  "incident.detail.trend.steady": "Stable",
  "incident.detail.trend.falling": "Easing",
  "incident.detail.worsening": "Worsening",
  "incident.detail.adv.title": "Advanced",
  "incident.detail.adv.intensity": "Radiative power",
  "incident.detail.adv.mw": "{n} MW",
  "incident.detail.adv.peak": "(peak {n} MW)",
  "incident.detail.adv.trendAxes": "Trend detail",
  "incident.detail.adv.weight": "Corroboration weight",
  "incident.detail.adv.identities": "Confirming authorities",
  "incident.detail.adv.axis.rising": "↑",
  "incident.detail.adv.axis.steady": "→",
  "incident.detail.adv.axis.falling": "↓",
  "incident.detail.wind": "{speed} km/h from {dir}",
  "compass.n": "N",
  "compass.ne": "NE",
  "compass.e": "E",
  "compass.se": "SE",
  "compass.s": "S",
  "compass.sw": "SW",
  "compass.w": "W",
  "compass.nw": "NW",
  "incident.source.nasa-firms": "NASA FIRMS",
  "incident.source.fogos.pt": "fogos.pt",
  "incident.source.anepc": "ANEPC",
  "now.activity.confirmedAgo": "confirmed {ago}",
  "now.activity.worsening": "▲ intensifying",
  // Install pointer (M3)
  "you.get.title": "GET BEACON ON YOUR PHONE",
  "you.get.body": "Install Beacon so alerts reach you even when it's closed.",
  "you.get.cta": "Get Beacon",
  "install.title": "Get Beacon on your phone",
  "install.desktop.intro": "Beacon warns you of nearby hazards even when the page is closed.",
  "install.desktop.scan": "Scan to open Beacon on your phone.",
  "install.ios.intro": "Install Beacon so alerts reach you even when it's closed.",
  "install.ios.step.share": "Tap the Share button.",
  "install.ios.step.add": "Choose “Add to Home Screen”.",
  "install.ios.step.open": "Open Beacon from your home screen.",
  "install.ios.note": "No app store, nothing to download.",
  "install.android.intro": "Install Beacon so alerts reach you even when it's closed.",
  "install.android.install": "Install Beacon",
  "install.android.apk": "Self-hosted APK (no Google)",
  "install.hint.title": "Install Beacon",
  "install.hint.body": "Get alerts even when Beacon is closed.",
  "install.hint.cta": "Install",
  "install.hint.dismiss": "Dismiss",
  // You-tab cleanup (field card + disclosures)
  "you.card.trusted": "Trusted neighbor",
  "you.card.building": "Becoming trusted",
  "you.card.neighbor": "Neighbor",
  "you.card.watch": "{n} km watch",
  "you.card.noHome": "No home area set",
  "you.loc.alertDetail": "Fine-tune alerts",
  "you.loc.livePosition": "Live position",
  "you.loc.level.off": "Off",
  "you.loc.level.info": "Info",
  "you.loc.level.warning": "Warning",
  "you.loc.level.critical": "Critical",
  "you.trust.howToggle": "How standing works",
  "you.trust.keysToggle": "Keys & recovery",
  "you.privacy.dataToggle": "Your data",
  "you.card.id": "Beacon ID",
  "you.loc.homeTitle": "HOME & PLACES",
  "you.loc.alertsTitle": "ALERTS",
  "you.loc.removeConfirm": "Remove?",
  "you.loc.cancel": "Cancel",
  // Advanced — raw-report layer (power-user opt-in)
  "you.raw.title": "Raw reports (advanced)",
  "you.raw.toggle": "Show individual reports on the map",
  "you.raw.warning": "Raw reports are far higher volume than confirmed incidents and use more data and battery. Turn on anyway?",
  "you.raw.depth.label": "How far back to load",
  "you.raw.depth.off": "Live only",
  "you.raw.depth.shallow": "Shallow",
  "you.raw.depth.medium": "Medium",
  "you.raw.depth.deep": "Deep",
  // Advanced — spread-cone debug cells (authoritative H3 overlay)
  "you.cone.title": "Spread-cone cells (advanced)",
  "you.cone.toggle": "Show the alert engine's exact cells",
  "you.cone.helper": "Overlays the authoritative H3 cells the alert engine uses to decide who is in a hazard's path — useful for checking why an alert did or didn't arrive.",

  "commons.card.title": "The commons",
  "commons.card.thisPeriod": "This period",
  "commons.card.runRate": "Recurring cost vs contributions",
  "commons.card.cumulative": "All time",
  "commons.card.oneTime": "One-time (e.g. development)",
  "commons.card.yourCost": "Your fair share so far",
  "commons.card.yourGiven": "You've chipped in",
  "commons.card.operatorStated": "operator-stated",
  "commons.card.chipIn": "Chip in",
  "commons.card.chippedIn": "You've chipped in this month — thank you.",
  "commons.card.need": "Beacon needed {amount} this month to alert you.",
  "commons.card.viewTransparency": "View transparency",
  "commons.card.deficit": "Deficit",
  "commons.card.surplus": "Surplus",
  "commons.card.empty": "No commons activity yet.",
  "commons.chipIn.title": "Chip in to the commons",
  "commons.chipIn.pickToken": "Choose what to send",
  "commons.chipIn.amount": "Amount (optional)",
  "commons.chipIn.getAddress": "Get address",
  "commons.chipIn.address": "Send to this address",
  "commons.chipIn.copy": "Copy address",
  "commons.chipIn.copied": "Copied",
  "commons.chipIn.deadline": "Pay within {min} min to be credited. Late payments still help the commons but won't show on your card.",
  "commons.chipIn.oneClick": "Pay with connected wallet",
  "commons.chipIn.noWallet": "No browser wallet detected — scan or paste the address.",
  "commons.chipIn.sent": "Transaction sent",
  "commons.chipIn.error": "Could not request an address. Try again.",
  "commons.chipIn.intro": "A one-off gift toward this instance's running costs — sent on-chain, no sign-up.",
  "commons.chipIn.network": "Send on {network}",
  "commons.chipIn.wrongChain": "Only send on this network — funds on another chain can't be detected.",
  "commons.chipIn.afterTitle": "After you send",
  "commons.chipIn.afterDetect": "Your deposit is detected automatically on-chain — nothing else to do on your end.",
  "commons.chipIn.afterAppear": "Once the network confirms (usually a minute or two) it appears in the commons, and on your card if you paid within the window.",
  "commons.chipIn.done": "Done",
  "commons.chipIn.close": "Close",
  "commons.received": "Contribution received — thank you!",
  "commons.chipIn.waiting": "Watching the chain for your deposit…",
  "commons.chipIn.receivedTitle": "Contribution received",
  "commons.chipIn.receivedBody": "Thank you for chipping in — it's on the commons ledger now.",
  "commons.privacy.hidden": "Hidden from everyone",
  "commons.privacy.public": "Hidden in public, briefly seen by this instance",
  "commons.privacy.custodial": "Your bank/provider sees this",
  "commons.transparency.title": "Commons transparency",
  "commons.transparency.intro": "Computed entirely from signed events on the firehose. Anyone can reproduce this independently.",
  "commons.transparency.series": "Recurring cost vs contributions, by period",
  "commons.transparency.oneTimeSeries": "One-time costs, by period",
  "commons.transparency.projection": "This period (estimate, pro-rata)",
  "commons.transparency.history": "History to genesis",
  "commons.transparency.basis": "Denominator basis",
  "commons.transparency.reproduce": "Reproduce this from the firehose: every input is a signed cost or contribution event.",
  "commons.transparency.colCost": "Recurring cost",
  "commons.transparency.colContrib": "Contributions",
  "commons.transparency.colNet": "Net",
  "commons.transparency.colOneTime": "One-time",
  "commons.transparency.estimate": "estimate",
};
